import { NavBar } from '@/v2/components/NavBar';
import PageWithTOC from '@/components/PageWithTOC';
import type { Section } from '@/components/PageWithTOC';
import { PRODUCT_EMAIL, PRODUCT_NAME } from '@/shared/constance';


const privacyPolicySections: Section[] = [
  {
    id: 'introduction',
    title: '',
    content: (
      <p className="text-base font-normal text-content-primary">
        STREAMLAKE PTE. LTD. (together with our affiliates, the "Company", "{PRODUCT_NAME}", "We",
        "Our", or "us") respect your privacy and are strongly committed to keeping secure any
        information we obtain from you or about you. This Privacy Policy describes our practices
        with respect to, and the basis on which we handle, your personal data which we collect from
        or about you. Your personal data refers to data whether true or not, about you which can be
        identified either (a) from that data or (b) from that data and other information to which we
        have or are likely to have access ("<span className="font-semibold">Data</span>"). By using
        the Services, including accessing or using the Platform, contacting or interacting with us
        or submitting information to us, you agree to the terms of this Policy and to the
        collection, use, disclosure and processing of your Data in accordance with this Policy. In
        this Policy, "you" or "your" refers to any user who purchase or use any of the Services,
        including any user of our Platform.
      </p>
    ),
  },
  {
    id: 'contents',
    title: 'CONTENTS OF THIS PRIVACY POLICY',
    content: (
      <div className="text-base font-normal text-content-primary">
        <ol className="list-decimal pl-4">
          <li>Data We Collect</li>
          <li>How We Use Your Data</li>
          <li>Our Legal Basis for Processing Data</li>
          <li>Data Storage and Retention</li>
          <li>Data Security and Protection</li>
          <li>To Whom We May Disclose or Transfer Your Data</li>
          <li>International Data Transfer</li>
          <li>Your Rights Relating to Your Personal Data</li>
          <li>Cookies</li>
          <li>Application</li>
          <li>Changes to the Policy</li>
          <li>Parental and Guardian Consent</li>
          <li>Contact Information</li>
          <li>Country / Region Specific Terms</li>
        </ol>
      </div>
    ),
  },
  {
    id: 'Data We Collect',
    title: '1. Data We Collect',
    content: (
      <div className="text-base font-normal text-content-primary">
        <p>
          We may collect a variety of Data from or about you or your devices from various sources,
          as described below.
        </p>
        <p>
          The types of Data we collect depend on how you use the Services. The Services offer
          optional features which, if used by you, require us to collect optional data to provide
          such features. You will be notified of such collection, as appropriate.
        </p>
        <p>
          If you do not provide your Data when requested, you may not be able to use our Services if
          that Data is necessary to provide you with our Services or if we are legally required to
          collect such Data.
        </p>
        <p className="font-semibold">A. Personal Data You Provide</p>
        <ul className="list-disc">
          <li>
            <span className="font-semibold">Account and profile information.</span> When you
            register for an account, you can use a third-party platform account (GitHub account) to
            log in, we will obtain the relevant information (including: user name, nickname, avatar,
            etc.) under the third-party account according to your authorization.We promise that the
            collection of the above information is used to provide you with account login services
            and to protect your account security and prevent security risks. If you refuse to
            authorize such information, you will not be able to use the account of the third party
            platform to log in to our platform.
          </li>
          <li>
            <span className="font-semibold">Content you provide through our products.</span> As part
            of the service, we collect and store the content you post, send, receive, and share
            through our product. This includes your Input (you can input via audio) and Output, such as questions,
            prompts,your codebase files and other content that you input, upload or submit to the
            Services, and the output that you create. This content may constitute or contain
            personal information, depending on the substance and how it is associated with your
            account. We use this information to generate and output new content as part of the
            Services.
            <p className="mt-2">
              Note:To provide you with codebase indexes, your codebase files will be uploaded to our
              servers to compute embeddings. We will retain and sore the embeddings and associated
              metadata and codebase files. If you don't want us to retain,you can choose delete
              codebase indexes ,once you choose delete,we will delete Your Codebase and the
              generated embeddings and associated metadatain our servers.
            </p>
            <p className="mt-2">
              When you interact with the Platform's integrated AI-chatbot, we collect any
              information (including any code snippets) that you choose to input.
            </p>
          </li>
          <li>
            <span className="font-semibold">Communication Information.</span> If you communicate
            with us, we will collect your email address,contact information, and the contents of any
            messages you send to us.
          </li>
          <li>
            <span className="font-semibold">Payment and billing information.</span> When you use
            certain paid services on {PRODUCT_NAME}, we collect your payment and billing
            information. You may also be required to provide credit card information to third-party
            secure payment processing service providers. We do not store your credit card
            information.
          </li>
        </ul>

        <p className="font-semibold">B. Personal Data We Automatically Collect</p>
        <ul className="list-disc">
          <li>
            <span className="font-semibold">Network and device information.</span> When you use our
            services, we may automatically collect your Data including user ID, password, token,
            digital certificate, IP address, device ID or characteristics, cookies, advertising
            identifiers, WiFi/WLAN Bluetooth, mobile network information, app and file names
            (subject to your consent).
          </li>
          <li>
            <span className="font-semibold">Usage Information.</span> We automatically collect
            information about how you engage with the Platform, including when and how you register
            and log-in to the Platform and the duration and frequency of your Platform usage. When
            you engage with our AI-chatbot, we also collect any feedback you give, AI-generated
            output, and associated metadata.
          </li>
          <li>
            <span className="font-semibold">Cookies.</span> We may collect your Data by using
            website cookies, in order to operate and administer our Services, and improve your user
            experience. A “cookie” is a piece of information sent to your browser by a website you
            visit. You can set your browser to accept cookies, to reject cookies. However, refusing
            a cookie may in some cases preclude you from using, or negatively affect the display or
            function of, a website or certain areas or features of a website.
          </li>
          <li>
            <span className="font-semibold">Analytics.</span> We may use a variety of online
            analytics products that use cookies to help us analyze how users use our Services and to
            enhance your experience when you use our Services.
          </li>
        </ul>
        <p>
          The categories and scope of data we collect about you may vary from time to time,
          depending on your choice of and interaction with {PRODUCT_NAME}, as well as the country or
          region where you use {PRODUCT_NAME}. You will be notified of such a variation, as
          appropriate.
        </p>
        <p>
          Where features are available, you may choose to browse certain content in the platform
          without logging in an account or registering, in which case we may still collect certain
          Data, including the content you browse, your interactions with the platform, and
          associated your device information such as device ID, WiFi information, and IP address.
        </p>
        <p className="font-semibold">C. Combination of Data</p>
        <p>
          By using our Services, you agree that we may combine the Data we receive from and about
          you, including the Data you provide and the Data we automatically collect as mentioned
          above, as well as Data we may receive from third parties to help us tailor our
          communication to you and to improve our Services. If you do not agree to this section,
          please do not use the Platform or any of the Services.
        </p>
        <p>
          You may be required to provide certain classes of Data to us under the following
          circumstances: (i) as required under any law, regulation or guideline; (ii) as required
          under any contract entered into between us, (iii) as required for us enter into any
          contract or agreement with you, (iv) or that you are otherwise obliged to provide the Data
          to us. If that is the case, we will inform you thereof separately, and will also explain
          the possible consequences if you fail to provide such Data to us. In all other cases,
          provision of the requested Data is optional, and may not affect your basic use of our
          general Services, but it may affect your ability to use specific Services, where the Data
          is needed for those purposes.
        </p>
      </div>
    ),
  },
  {
    id: 'how-we-use-your-data',
    title: '2. How We Use Your Data',
    content: (
      <div className="text-base font-normal text-content-primary">
        <p>
          We may use your Data for a number of purposes associated with operating our business and
          providing the Services to you, including:
        </p>
        <ul className="list-disc">
          <li>
            <span className="font-semibold">Identification and authentication.</span> We use your
            Data to verify your identity or eligibility to access the features of {PRODUCT_NAME}{' '}
            when you access and use our Services, such as receiving rewards or prizes, or when you
            otherwise interact or engage with us, in order to protect the security of your Data. In
            some instances, we use your Data to verify your age in order to provide certain Services
            to you, or to verify your identity in order to fulfill your request in relation to your
            Data.
          </li>
          <li>
            <span className="font-semibold">Provision of Service.</span> We process your Data to
            provide you with the Services that you request, to respond to your queries, or to
            contact you about changes to {PRODUCT_NAME} and the platform or any of our Services, and
            to otherwise manage our relationship with you. This may also include managing the
            content ecosystem across {PRODUCT_NAME}, or aspects, thereof, facilitating third party
            partnerships or integrations across {PRODUCT_NAME}, monitoring and testing{' '}
            {PRODUCT_NAME}, troubleshooting, or allowing you and other users to connect with each
            other.
          </li>
          <li>
            <span className="font-semibold">Payment processing.</span> We may use your Data for
            billing and payment processing purposes or to distribute awards to you or to recover
            monies owed to us. This may also include enabling payment through third-party payment
            systems.
          </li>
          <li>
            <span className="font-semibold">
              Service improvement, research and feature development.
            </span>{' '}
            We inspect and analyze usage information and statistics to maintain and improve
            {PRODUCT_NAME}. We may also use your Data to support customer services and implement
            user feedback, conduct surveys, or improve, research and develop new products and
            services or new methods of processes for our business operations.
          </li>
          <li>
            <span className="font-semibold">Communications.</span> We may use your Data to contact
            and communicate with you. Communications may include, for example, providing information
            about our Services, changes to this Policy, changes to our Terms of Service, changes to
            our Terms of Paid Service, updates regarding our Services, user information reports on
            users who are minors, or responses to your questions, comments or requests.
          </li>
          <li>
            <span className="font-semibold">Content safety.</span> To ensure the safety, security
            and stability of {PRODUCT_NAME} we may scan, analyze, and review User Content to detect
            and combat harmful activity and content, frauds, illegal activity or content on this
            Platform.
          </li>
          <li>
            <span className="font-semibold">Preferences, promotions and sales.</span> We may use
            your Data to build a profile about you, to analyze and understand your preferences, to
            help determine which marketing materials would be of interest to you, to display
            advertising, to conduct award activities, to measure the effectiveness of advertising
            and promotions, and to facilitate sales, purchases and delivery (where applicable). We
            may also process your Data in the above ways for conducting sweepstakes, contests or
            other promotions.
          </li>
          <li>
            <span className="font-semibold">Exercising our rights.</span> We may use your Data to
            exercise our rights where it is necessary to do so, for example to detect, prevent,
            investigate and respond to security incidents or breaches, data scrapping, fraud, abuse,
            intellectual property infringement claims and any other claims, financial crimes, other
            violations of law, violations of our Terms of Service or violations of our Terms of Paid
            Service.
          </li>
          <li>
            <span className="font-semibold">Complying with our obligations.</span> We may process
            your Data to comply with legal or regulatory obligations, codes of practice, guidelines
            or rules, including without limitation our legal obligation to prevent users under the
            Minimum Age (as defined below) from accessing the Platform or the Apps, to respond to
            lawsuits, claims or investigations, to assist in law enforcement and investigations
            conducted by any governmental and/or regulatory authority in any instance of mergers,
            acquisitions, sales and asset transfers, insolvency proceedings, corporate
            restructuring, winding up or liquidation, whether within or outside your country or
            region. We may also aggregate and/or de-identify your Data and may use such aggregate or
            identified information for any other purposes to the extent permitted under applicable
            law.
          </li>
        </ul>
      </div>
    ),
  },
  {
    id: 'legal-basis',
    title: '3. Our Legal Basis for Processing Data',
    content: (
      <div className="text-base font-normal text-content-primary">
        <p>
          In order to process your Data, we will rely upon the legal bases afforded by applicable
          laws and regulations. We only process your Data when we have a valid "legal basis,"
          including as set forth below.
        </p>
        <ul>
          <li>
            <span className="font-semibold">Consent.</span> We may process your Data where you have
            consented to certain processing of your Data. For example, we may process your Data to
            use cookies where you have consented to such use.
          </li>
          <li>
            <span className="font-semibold">Contractual necessity.</span> We may process your Data
            where required to provide you with our Services requested by you, or where necessary for
            the conclusion or performance of any transaction between you and us. For example, we may
            need to use or process your Data to respond to your inquiries or requests.
          </li>
          <li>
            <span className="font-semibold">Compliance with legal obligations.</span> We may process
            your Data where we have a legal obligation to do so. For example, we may process your
            Data to comply with tax, labor and accounting obligations.
          </li>
          <li>
            <span className="font-semibold">Legitimate Interests.</span> We may process your Data
            where we or a third party have a legitimate interest in processing your Data.
            Specifically, we have a legitimate interest in using your Data for product development
            and internal analytics purposes, and otherwise to improve the safety, security, and
            performance of our Services. We only rely on our or a third party's legitimate interests
            to process your Data when these interests are not overridden by your rights and
            interests.
          </li>
        </ul>
      </div>
    ),
  },
  {
    id: 'data-storage',
    title: '4. Data Storage and Retention',
    content: (
      <div className="text-base font-normal text-content-primary">
        <ul>
          <li>
            We store your Data on our servers located in Singapore. We also have support,
            engineering, content moderation, and other functional teams located around the world to
            support the operation of the Platform and the provision of Services to you. We have
            rigorous internal control measures in place to strictly limit data access to designated
            personnel only.
          </li>
          <li>
            We will retain your Data for as long as necessary:
            <ul className="mt-2 list-disc pl-8">
              <li>to achieve the purposes as set out in this Policy;</li>
              <li>
                as required by applicable laws and regulations, including any requirements to retain
                data for a certain period of time, taking into account the statute of limitations
                under applicable laws and regulations;
              </li>
              <li>
                for our legitimate and lawful purposes, including any business, compliance and legal
                purposes; and/or
              </li>
              <li>to defend against any actual, threatened or potential claims or disputes.</li>
            </ul>
          </li>
          <li>
            We will not retain your Data for longer than necessary or beyond the end of the purposes
            for which you provided your Data to us, unless we are required or permitted to do so
            under applicable laws. In addition, we reserve the right to delete, destroy, or
            de-identify your Data that is no longer necessary or current for the purposes for which
            it was obtained.
          </li>
          <li>
            We may retain some of your Data after you cease to use our Services, for instance if the
            Data is necessary to meet our legal obligations, such as retaining the information for
            tax and accounting purposes.
          </li>
          <li>
            Where applicable, we will delete your Data upon your request, unless we are required to
            retain such Data under applicable laws. We are entitled at all times to delete your Data
            without notice. In such cases, we owe no compensation to you as a result of the deletion
            of your personal data.
          </li>
          <li>
            If you want further information about the retention periods applied to your personal
            data, you may contact us at our address listed in the "Contact Information" section of
            this Policy.
          </li>
        </ul>
      </div>
    ),
  },
  {
    id: 'data-security',
    title: '5. Data Security and Protection',
    content: (
      <div className="text-base font-normal text-content-primary">
        <ol className="list-decimal space-y-4 pl-4">
          <li>
            We will take appropriate technical and organizational measures to help protect and
            secure your Data. We will endeavor to protect your Data against unauthorized or unlawful
            processing, accidental loss, destruction, or damage through these measures, including:
            <ul className="mt-2 list-disc space-y-2 pl-8">
              <li>using encryption techniques;</li>
              <li>
                establishing specialized systems and procedures, such as strictly limiting the scope
                of personnel with access to your Data, and requiring those who have such access to
                comply with relevant confidentiality obligations;
              </li>
              <li>
                forming a specialized department in charge of information security. In the event of
                incidents such as information leakage or data or security breach, we will take
                timely measures to handle such incidents, and we will notify you promptly in an
                appropriate manner, including through messages or announcements.
              </li>
            </ul>
          </li>
          <li>
            We will endeavor to ensure that, by default, we only collect and process your Data
            needed to provide you access to the Platform and our Services. We also apply this
            principle of data privacy and protection controls to our processing activities. This
            means that we will neither publish nor share your Data by default.
          </li>
          <li>
            While we are committed to protecting your Data, no electronic data transmission or
            storage of information can be completely secure. We kindly request you do not disclose
            certain Data, such as passwords, credit card numbers, PINs or other confidential data
            during your use of our Services. Our commitment to safeguarding your privacy is
            unwavering, but the security of Data also relies on your safe practices. Therefore,
            although we take appropriate technical and organizational measures to secure your Data,
            we do not guarantee, and you should not expect, that your Data will always remain
            secure. In particular, any email sent to or from us may not be secure. Therefore, you
            should take special care in deciding what information you send to us via the Services or
            email. In addition, we are not responsible for circumvention of any privacy settings or
            security measures contained on the Service, or third-party websites. If you do not agree
            to these risks and conditions, you must discontinue your use of {PRODUCT_NAME}.
          </li>
          <li>
            We will retain your Data for only as long as we need in order to provide our Services to
            you, or for other legitimate business purposes such as resolving disputes, safety and
            security reasons, or complying with our legal obligations. How long we retain Data will
            depend on a number of factors, such as the amount, nature, and sensitivity of the
            information, the potential risk of harm from unauthorized use or disclosure, our purpose
            for processing the information, and any legal requirements.
          </li>
        </ol>
      </div>
    ),
  },
  {
    id: 'data-disclosure',
    title: '6. To Whom We May Disclose or Transfer Your Data',
    content: (
      <div className="text-base font-normal text-content-primary">
        <ol className="list-decimal space-y-4 pl-4">
          <li>
            We will not share or transfer any of your Data to any third parties except as explicitly
            provided in this Privacy Policy. We will only share your Data with third parties in the
            following circumstances:
            <ul className="mt-2 list-disc space-y-2 pl-8">
              <li>
                when approved or authorized by you or subject to the "Parental and Guardian Consent"
                section below, your parent or legal guardian;
              </li>
              <li>
                as required for administrative or judicial purposes, including any requirements of
                competent authorities, courts and regulatory bodies;
              </li>
              <li>
                in any legal proceedings (including any arbitration) involving us in which we aim to
                protect our legal rights;
              </li>
              <li>where expressly allowed by the Terms of Service;</li>
              <li>
                in order to protect the interests of the Company, or interests of any individual or
                the interests of the public against harm;
              </li>
              <li>for further archiving or de-identification with other third parties.</li>
            </ul>
          </li>
          <li>
            We may share your Data with our corporate affiliates, group members, and other third
            parties, who will only use your Data in accordance with this Policy to facilitate the
            provision of {PRODUCT_NAME} to you or for other purposes as indicated below:
            <ul className="mt-2 list-disc space-y-2 pl-8">
              <li>
                <span className="font-semibold">
                  In the context of a transaction including mergers, acquisitions or asset sales:
                </span>{' '}
                We may transfer your Data to another legal entity for the purposes of a
                collaboration, joint venture, corporate reorganization, change of legal form or
                other similar event or transaction. In order to develop our business, we may engage
                in transactions such as mergers, acquisitions, sales and asset transfers. In these
                circumstances, we will inform you of the relevant transactions, and will continue to
                protect or require the successor to protect your Data in accordance with applicable
                laws and regulations and this Policy.
              </li>
              <li>
                <span className="font-semibold">Third parties in support of {PRODUCT_NAME}:</span>{' '}
                We share information with third parties who help us operate, provide, improve,
                integrate, customize, support, and market our services. For example, to provide
                services to you, we may share information with third-party service partners who
                provide consulting support. We work with third-party service providers who provide
                website and application development, hosting, maintenance, backup, storage, virtual
                infrastructure, payment processing, analytics, and other services to us. Such
                services may require the service provider to access or use information about you. We
                may also share your Data with professional agencies and individuals that we engage
                to provide professional services to us, including advisors, auditors, lawyers and
                accountants. We will only share your Data on a need-to-know basis with third parties
                in order for them to perform corresponding functions and services, and we will
                request such parties to maintain appropriate security measures to protect such Data
                from unauthorized access or processing.
              </li>

              <li>
                <span className="font-semibold">Third-Party Platforms and Partners. </span>
                We may share certain Information You Provide, Technical Information, and Usage
                Information with third parties whose platforms or services are integrated with the
                Platform. For example, if you sign up or log in to the Platform using your
                third-party account details, we will share certain Technical Information to
                facilitate account sign up or login. If you choose to deploy your projects using
                third-party integrations, we will share the codebase files of your selected projects
                to facilitate the deployment.
              </li>

              <li>
                <span className="font-semibold">Legal, regulatory or government authorities:</span>{' '}
                We may in limited cases transfer your Data to local (where you reside) or overseas
                authorities such as legal, regulatory, law enforcement, government and tax
                authorities, to satisfy our obligations or those of third parties (when they receive
                a request from an authority empowered by law to do so), pursuant to applicable laws
                and regulations.
              </li>
            </ul>
          </li>
          <li>
            Except as otherwise provided in this Policy, we will not disclose any of the Data
            collected from your use of {PRODUCT_NAME} to the public. If public disclosure is
            required by applicable laws and regulations, we will inform you of the purpose of the
            disclosure and the type of information disclosed.
          </li>
          <p className="mt-4 font-semibold">Legal Reasons</p>
          <p>
            We will share the Data if we have a good-faith belief that access, use, preservation, or
            disclosure of such Data is reasonably necessary to:
          </p>
          <ul className="mt-2 list-disc space-y-2 pl-8">
            <li>
              comply with any applicable law, regulation, or to commence or respond to any legal
              process/procedure, lawsuit or enforceable governmental request,
            </li>
            <li>protect and defend our rights or property,</li>
            <li>prevent fraud or criminal activity,</li>
            <li>
              respond under urgent circumstances to protect the personal safety of any entity,
              person or group, or
            </li>
            <li>to avoid or mitigate legal liability.</li>
          </ul>
          <p className="mt-4 font-semibold">Business Transfers</p>
          <p>
            If we are involved in a reorganization, merger, acquisition, sales of assets or
            liquidation, we will continue to ensure the confidentiality of your Data and notify
            affected users that their Data will be transferred or be subject to a different privacy
            policy in advance.
          </p>
        </ol>
      </div>
    ),
  },
  {
    id: 'international-data-transfer',
    title: '7. International Data Transfer',
    content: (
      <div className="text-base font-normal text-content-primary">
        <p>
          We may transfer your Data to countries or regions located outside of the country where you
          reside solely for the purposes set out in this Policy, and in accordance with this Policy
          and applicable laws and regulations. Data disclosure as referred to in this Policy may
          involve a transfer of your Data to countries and regions that provide different levels of
          data protection. Applicable legal and regulatory requirements are taken into consideration
          before any cross-border transfer of your Data takes place and our policies are as follows:
        </p>
        <p>
          We may share your Data with our other group entities in other jurisdictions for internal,
          business and operational purposes in order to operate the {PRODUCT_NAME} and provide you
          access to the Services. We will ensure, by way of appropriate contractual guarantees which
          are compliance with applicable laws and regulations, that all such entities with which we
          share your Data maintain appropriate security measures to protect your Data from
          unauthorized access or processing and will not share this Data with any third parties
          unless you expressly agree otherwise.
        </p>
      </div>
    ),
  },
  {
    id: 'your-rights',
    title: '8. Your Rights Relating to Your Personal Data',
    content: (
      <div className="text-base font-normal text-content-primary">
        <ol className="list-decimal space-y-4 pl-4">
          <li>
            Once you have completed your Account registration and put in place reasonable and
            necessary authentication measures, you can review, modify, and delete the Data you have
            submitted to us by contacting us using the contact information provided in the "Contact
            Information" section of this Policy. In general, you can browse, modify, and delete your
            own information at any time, but you may not be able to modify certain initial
            registration information and verification information provided at the time of
            registration for security and identification purposes.
          </li>
          <li>
            You may have the following rights to the extent applicable under local laws and
            regulations which you can exercise by contacting us using the contact information
            provided in the "Contact Information" section of this Policy:
            <ul className="mt-2 list-disc space-y-2 pl-8">
              <li>
                You have the right to be informed in a concise, transparent, intelligible and easily
                accessible form of the way in which your Data is processed. You also have the right
                to obtain confirmation as to whether or not Data concerning you is being processed,
                and where that is the case, to access such Data and obtain a copy thereof.
              </li>
              <li>
                You have the right to request rectification of your Data. You also have the right to
                have incomplete Data completed.
              </li>
              <li>
                Where you have provided your consent, you have the right to withdraw your consent.
                If you withdraw your consent, we may continue processing your Data to the extent
                permitted by applicable laws and/or set out in this Policy.
              </li>
              <li>
                You have the right to receive your Data in a structured, commonly used, and
                machine-readable format and have the right to transmit those data to another
                controller.
              </li>
              <li>
                You have the right to request the deletion or restriction of your Data, as permitted
                by law.
              </li>
              <li>
                You have the right to object to our processing of your Data, for example, in case we
                process your data for direct marketing purposes, as permitted under applicable laws.
              </li>
              <li>
                You have the right to delete your Account, which is an irreversible process as you
                will not be able to access your Account upon deletion, and all data in relation to
                the Account will be deleted or de-identified, subject to any statutory retention
                requirements under applicable laws and regulations. If you wish to have your Account
                deleted, you can contact us by using the contact information provided in "Contact
                Information" of this Policy.
              </li>
              <li>
                You may also have other rights as described under the "Country / Region Specific
                Terms" section below or otherwise under the local laws.
              </li>
            </ul>
          </li>
          <li>
            We will verify your identity first before we can fulfill your request. We will respond
            to your request within the timeframe as required or permitted by the local laws.
          </li>
          <li>
            You may also be able to change your cookie settings as described in the section below to
            manage the Data we collect using cookies.
          </li>
          <li>
            You can prevent us from obtaining your Data by suspending certain functions provided by
            our Services.
          </li>
        </ol>
        <p className="mt-4">
          You may exercise the abovementioned rights free of charge by contacting us at the contact
          details in the "Contact Information" section of this Policy. If requests are manifestly
          unfounded or excessive, in particular because of the repetitive nature of the requests, we
          will either charge you a reasonable fee or refuse to comply with the request.
        </p>
        <p className="mt-4">
          We will provide you with information about the follow-up to the request without undue
          delay and in general, within one month of receipt of the request. The applicable privacy
          laws may allow or require us to refuse your request. If we cannot comply with your
          request, we will inform you of the reasons why, subject to any legal or regulatory
          restrictions.
        </p>
        <p className="mt-4">
          These rights are not absolute and they may be limited in some situations. For example,
          where we can demonstrate that we have a legal requirement to process your data or if
          making the information available to you would reveal personal data about another person or
          if we are legally prevented from disclosing such information. In some instances, this may
          mean that we are able to retain your Data even if you withdraw your consent.
        </p>
        <p className="mt-4">
          We hope that we can satisfy any queries you may have about the way we process your Data.
          In the event you still have unresolved concerns, you also have the right to lodge a
          complaint with a supervisory authority, in particular to the data protection authority in
          the country of your habitual residence or place of work.
        </p>
      </div>
    ),
  },
  {
    id: 'cookies',
    title: '9. Cookies',
    content: (
      <div className="text-base font-normal text-content-primary">
        <p>
          Cookies are pieces of information that any website or app may transfer to your browser,
          which are then stored in your computer or mobile system. For example, the deposit of
          cookies enhances your online experience by allowing us to save your preferences.
        </p>
        <p className="mt-4">We use cookies and automatically collected information to:</p>
        <ol className="mt-2 list-decimal space-y-2 pl-4">
          <li>
            personalize our Services, such as remembering your Data so that you will not have to
            re-enter it during your use of, or the next time you use, our Services;
          </li>
          <li>
            provide customized advertisements, content, and information adapted to your profile;
          </li>
          <li>
            monitor and analyze the effectiveness of our Services and third-party marketing
            activities adapted to your profile;
          </li>
          <li>
            monitor aggregate site usage metrics such as total number of visitors and pages viewed;
            and
          </li>
          <li>
            track your entries, submissions, and status in any promotions or other activities
            offered through our Services (services adapted to your profile).
          </li>
        </ol>
        <p className="mt-4">
          Tracking technology also helps us manage and improve the usability across {PRODUCT_NAME}.
          We may also collect your shortened IP address on a non-readable basis, which does not
          allow us to directly identify you.
        </p>
        <p className="mt-4">
          If you do not want us to collect your Data through the use of cookies, you can set your
          web browser to reject cookies from {PRODUCT_NAME}. Each browser is different, so you
          should check your browser's "Help" menu to learn how to change your cookie preferences.
          Please note that if you reject cookies, you may still use our Services, but your ability
          to access certain areas of our Services may be limited or restricted.
        </p>
        <p className="mt-4">
          If you have questions regarding the specific information about you that we process or
          retain, as well as your choices regarding our collection and use practices, please contact
          us using the information listed below.
        </p>
      </div>
    ),
  },
  {
    id: 'application',
    title: '10. Third Party Application',
    content: (
      <div className="text-base font-normal text-content-primary">
        <p>This Policy only applies to our Services.</p>
        <p className="mt-4">
          To better enrich your experience, our Service may contain links to other websites not
          operated or controlled by us (including our affiliates), including payment services. You
          can choose whether to access such content or links, or whether to use the third party's
          products or services. However, we do not have control over the products or services
          provided by third parties. Our provision of these links do not imply that we endorse or
          have reviewed these third-party services. Our Policy does not govern or apply to the data
          protection issues in connection with your use of any third-party services or websites. We
          strongly advise you to review the privacy policy of every site you visit.
        </p>
        <p className="mt-4">
          If any provisions from this Policy are in conflict with any applicable laws, such
          provisions will be replaced by provisions of the same purport that reflects the original
          intention of the provision, to the extent permissible under the applicable laws. In that
          case, the remaining provisions remain applicable and unchanged.
        </p>
      </div>
    ),
  },
  {
    id: 'changes',
    title: '11. Changes to the Policy',
    content: (
      <div className="text-base font-normal text-content-primary">
        <p>
          We may update, change or modify the terms hereof from time to time, and such changes shall
          form a part of this Policy. We will notify you about those changes through general
          announcements on our Services or by sending a notification to you when you log into or
          update the Website，and may obtain your consent, consistent with the significance of the
          changes we make and as required by applicable law. Unless otherwise indicated, any update,
          change or modification will become effective on the same day of notification and apply to
          all of your Data.
        </p>
      </div>
    ),
  },
  {
    id: 'parental-consent',
    title: '12. Parental and Guardian Consent',
    content: (
      <div className="text-base font-normal text-content-primary">
        <p>
          Our Services are not intended for or directed to children as defined by the applicable
          laws in your country or region. For example, in South Korea, "children" refer to
          individuals under 14 years old, whereas in some other countries or regions, it refers to
          individuals under 13 years old ("Minimum Age"). If you are above the Minimum Age but under
          the age of majority in your jurisdiction ("Majority Age"), you need your parent or
          guardian's permission and registration to use our Services. Their consent means they agree
          to this Policy on your behalf.
        </p>
        <p className="mt-4">
          We do not knowingly collect or solicit Data from children under the Minimum Age. If you
          are a guardian and discover that an individual under your care has used our Services or
          provided personal Data, please terminate their use of our Services immediately and notify
          us promptly at the contact details listed in the "Contact Information" section of this
          Policy so we can take appropriate action. If you wish to query or delete the Data, please
          contact us through the "Contact Information" section of this Policy. Our customer service
          team may need to verify and confirm the guardianship. Once confirmed, we will assist you
          in deleting the Data and the corresponding Account as per your request.
        </p>
      </div>
    ),
  },
  {
    id: 'contact-information',
    title: '13. Contact Information',
    content: (
      <div className="text-base font-normal text-content-primary">
        <p>
          If you have any complaints, suggestions or questions regarding this Policy or in relation
          to the collection, use, disclosure, storage, protection and other forms of processing of
          your Data, please contact us through email at{' '}
          <a href={`mailto:${PRODUCT_EMAIL}`}>{PRODUCT_EMAIL}</a> or by accessing
          <a href="https://x.com/CodeFlicker_ai">https://x.com/CodeFlicker_ai</a>.
        </p>
        <p className="mt-4">
          If a data protection officer is required to be appointed under the local law of your
          jurisdiction, you may also contact the data protection officer using the contact details
          provided in the "Country / Region Specific Terms" section below.
        </p>
        <p className="mt-4">
          We will review the issues involved as soon as possible and reply to you in a timely manner
          as required by applicable laws. If you have any concerns regarding how we are handling
          your Data, please contact us. We will endeavor to address your concerns promptly.
        </p>
      </div>
    ),
  },
  {
    id: 'country-specific',
    title: '14. Country / Region Specific Terms',
    content: (
      <div className="text-base font-normal text-content-primary">
        <p>
          In the event of a conflict between the provisions of Country / Region Specific Terms that
          are relevant to your jurisdiction from which you access or use the Services, and the rest
          of this Policy, the relevant jurisdiction's Country / Region Specific Terms will supersede
          and prevail to the extent of such conflict.
        </p>
        <p className="mt-4 font-semibold">California (United States)</p>
        <p>
          If you are a California resident, the following additional privacy disclosures under the
          California Consumer Privacy Act of 2018 ("CCPA") and other California laws may be
          applicable to you and allow you to exercise your rights regarding your personal data.
        </p>
        <p className="mt-4 font-semibold">1. Introduction</p>
        <p>
          If the CCPA is applicable, you have the right to know and understand how we collect, use,
          disclose, and share your Data, to access your Data, to request that we delete specific
          Data, to exercise your rights to opt-out, and also not be discriminated against when
          exercising your privacy rights granted under the CCPA.
        </p>
        {/* 由于内容过长，这里省略了加州具体条款的其他内容 */}
        <p className="mt-4 font-semibold">Singapore</p>
        <p className="mt-2 font-semibold">Data Protection Officer</p>
        <p>
          If you wish to access your Data, correct any Data in our possession, withdraw your consent
          for the collection, use, or disclosure of your Data, or if you have any queries or
          feedback on this Policy, please contact our Data Protection Officer in the following
          manner:
        </p>
        <p className="mt-2">Attention: Data Protection Officer</p>
        <p>
          Email Address: <a href={`mailto:${PRODUCT_EMAIL}`}>{PRODUCT_EMAIL}</a>
        </p>
        <p className="mt-4 font-semibold">International transfer of Data</p>
        <p>
          If you live in Singapore or if we collect or store your Data in Singapore, before
          transferring any of your Data outside of Singapore, we will take appropriate steps to
          ensure that the recipient is bound by legally enforceable obligations to provide to the
          transferred personal data a standard of protection that is at least comparable to that
          under the Personal Data Protection Act 2012 of Singapore.
        </p>
        <p className="mt-4 font-semibold">Access and correction</p>
        <p>
          If you wish to make (a) an access request for access to a copy your Data or information
          about the ways in which we use or disclose your Data, or (b) a correction request to
          correct or update any of your Data which we hold about you, you may submit your request to
          our Data Protection Officer at the contact details above. We will respond to your request
          as soon as reasonably possible. Should we not be able to respond to your request within
          thirty (30) days after receiving your request, we will inform you in writing within thirty
          (30) days of the time by which we will be able to respond to your request. If we are
          unable to provide you with any Data or to make a correction requested by you, we shall
          generally inform you of the reasons why we are unable to do so (except where we are not
          required to do so under applicable laws).
        </p>
      </div>
    ),
  },
];

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="size-full">
      <NavBar />
      <PageWithTOC
        title="Privacy Policy"
        subtitle="Release Date: 2025/08/04   Effective Date: 2025/08/04"
        sections={privacyPolicySections}
      />
      {/* <Signature className="fixed bottom-[244px] right-[120px]" /> */}
    </div>
  );
};

export default PrivacyPolicy;